import { createRouter, createWebHistory } from "vue-router";
// Layouts
import ErrorLayout from "@/layouts/app/Error.vue";
import BasicLayout from "@/layouts/app/BasicLayout.vue";
import Authenticated from "@/layouts/app/Authenticated.vue";

// Components
import HomeView from "@/views/app/HomeView.vue";
import Profil from "@/views/app/profil/ProfilView.vue";
import Contact from "@/views/app/ContactView.vue";
import Company from "@/views/app/company/CompanyView.vue";
// |_ Campaigns
import NewCampaignView from "@/views/app/campaigns/newCampaign/NewCampaignView.vue";
import CampaignView from "@/views/app/campaigns/singleCampaign/SingleCampaignView.vue";
import AddCandidatesView from "@/views/app/campaigns/singleCampaign/AddCandidates.vue";
import EditCandidatesView from "@/views/app/campaigns/singleCampaign/EditCampaignView.vue";
// |_ Spaces
import Spaces from "@/views/app/spaces/SpacesView.vue";
import Space from "@/views/app/spaces/SpaceView.vue";
// |_ Not autenticathed
import AuthView from "@/views/auth/AuthView.vue"
import InvitationView from "@/views/auth/InvitationView.vue";

// |_ Errors
import NotFoundPage from "@/views/app/errors/404.vue";
import MaintenancePage from "@/views/app/errors/Maintenance.vue";
// |_ Others
import IntegrationPage from "@/views/app/integration/IndexView.vue";
import NoCompanyView from "@/views/app/NoCompanyView.vue";
// Stores
import { useAppStore } from "@/stores/app.js";

import { i18n } from "@/i18n";

// Navigation guards
import { authGuard } from "@/guards/auth.guard";
import { maintenanceGuard } from "@/guards/maintenance.guard";

const { t } = i18n.global;

const appRouter = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: "/404",
      name: "notFoundPage",
      component: NotFoundPage,
      meta: {
        layout: ErrorLayout,
        document_title: t("app.router.notFound.documentTitle"),
      },
    },
    {
      path: "/maintenance",
      name: "maintenancePage",
      component: MaintenancePage,
      meta: {
        layout: ErrorLayout,
        document_title: t("app.router.maintenance.documentTitle"),
      },
    },
    // Not authenticated
    {
      path: "/auth",
      name: "auth",
      component: AuthView,
      meta: {
        layout: BasicLayout,
        isPublic: true,
        isTransverse: true,
        document_title: t("app.router.login.documentTitle"),
      },
    },
    {
      path: "/join",
      name: "invitation",
      component: InvitationView,
      meta: {
        layout: BasicLayout,
        isPublic: true,
        isTransverse: true,
        document_title: t("app.router.login.documentTitle"),
      },
    },
    // Authenticated
    {
      path: "/",
      name: "root",
      component: null,
      meta: {
        document_title: t("app.router.home.documentTitle"),
      },
    },
    {
      path: "/no-company",
      name: "noCompany",
      component: NoCompanyView,
      meta: {
        layout: BasicLayout,
        isPublic: true,
        isTransverse: true,
        document_title: 'Yuzu',
      },
    },
    {
      path: "/companies/:companyId",
      name: "home",
      component: HomeView,
      meta: {
        document_title: t("app.router.home.documentTitle"),
        title: t("app.router.home.title"),
      },
    },
    {
      path: "/companies/:companyId/newCampaign",
      name: "newCampaign",
      component: NewCampaignView,
      meta: {
        title: t("app.router.newCampaign.title"),
        customHelpCenter: true,
        document_title: t("app.router.newCampaign.documentTitle"),
      },
    },
    {
      path: "/companies/:companyId/spaces",
      name: "spaces",
      component: Spaces,
      meta: {
        title: t("app.router.spaces.title"),
        document_title: t('app.router.spaces.documentTitle'),
      },
    },
    {
      path: "/companies/:companyId/spaces/:spaceId",
      name: "space",
      component: Space,
      meta: {
        document_title: t("app.router.space.documentTitle"),
        parent: {
          name: "spaces",
        },
        customTitle: {
          store: "space",
          data: "currentSpace.name",
          fallback: "Yuzu - principal",
        },
      },
    },
    {
      path: "/companies/:companyId/spaces/:spaceId/campaigns/:campaignId",
      name: "campaign",
      component: CampaignView,
      meta: {
        document_title: t("app.router.campaign.documentTitle"),
        parent: {
          name: "space",
        },
        customTitle: {
          store: "campaign",
          data: "currentCampaign.name",
        },
      },
    },
    {
      path: "/companies/:companyId/spaces/:spaceId/campaigns/:campaignId/edit",
      name: "editCampaign",
      component: EditCandidatesView,
      meta: {
        document_title: t("app.router.editCampaign.documentTitle"),
        parent: {
          name: "campaign",
        },
        customTitle: {
          store: "campaign",
          data: "currentCampaign.name",
        },
      },
    },
    {
      path: "/companies/:companyId/spaces/:spaceId/campaigns/:campaignId/candidates",
      name: "addCandidates",
      component: AddCandidatesView,
      meta: {
        parent: {
          name: "campaign",
        },
        document_title: t("app.router.addCandidates.documentTitle"),
        title: t("app.router.addCandidates.title"),
      },
    },
    {
      path: "/companies/:companyId/profil",
      name: "profil",
      component: Profil,
      meta: {
        document_title: t("app.router.profil.documentTitle"),
        title: t("app.router.profil.title"),
      },
    },
    {
      path: "/companies/:companyId/settings",
      name: "company",
      component: Company,
      meta: {
        document_title: t("app.router.company.documentTitle"),
        title: t("app.router.company.title"),
        permissions: ["billingHistory", "members"],
      },
    },
    {
      path: "/companies/:companyId/contact",
      name: "contact",
      component: Contact,
      meta: {
        document_title: t("app.router.contact.documentTitle"),
        title: t("app.router.contact.title"),
      },
    },
    {
      path: "/integration-page",
      name: "integrationPage",
      component: IntegrationPage,
      meta: {
        title: "Page d'intégration",
        layout: ErrorLayout,
      },
    },
    // Onboarding
    {
      path: "/companies/:companyId/spaces/:spaceId/campaigns/8d9740ed-56ac-4f1f-856f-df9d81f16c52",
      name: "campaign-demonstration",
      component: CampaignView,
      meta: {
        document_title: t("app.router.campaign.documentTitle"),
        parent: {
          name: "space",
        },
        customTitle: {
          store: "onboarding",
          data: "currentCampaign.name",
        },
      },
    },
    // Others
    {
      path: "/:pathMatch(.*)*",
      name: "not-found",
      redirect: { name: "notFoundPage" },
      meta: {
        layout: ErrorLayout,
      },
    },
  ],
});

const guards = [maintenanceGuard, authGuard];

appRouter.beforeEach(async (to, from) => {
  // Reset some app state
  const appStore = useAppStore();
  appStore.talentResultPanelIsOpen = false;
  appStore.currentRoute = to;

  // Run guards
  for (const guard of guards) {
    const result = await guard(to, from)
    if (result) return result;
  }
});

export default appRouter;
